import { create } from '@bufbuild/protobuf'
import {
  type ImportSupply48DataRow,
  ImportSupply48DataRowSchema,
  Supply48SlotValueSchema,
} from '@ebalance/proto/bffuser/v1/supply_48_data_model_pb'
import { DateSchema } from '@ebalance/proto/google/type/date_pb'

import { KOMA_COUNT } from '~/constants/koma'
import { escapeCsvCell } from '~/libs/csv'
import { isValidCivilDate } from '~/models/civilDate'
import { readCsvRecords } from '~/models/csvRecords'

/**
 * 48点データ CSV の frontend parse / build ユーティリティ。
 *
 * 取込 (supply-48-data-csv-import) の parse と、48 点データ画面からの
 * エクスポート (AC-S4D-007) の build を同一ファイルに置く (= 列構成が
 * 片側だけ変わると往復が壊れるため、フォーマットの SoT を 1 箇所にする)。
 *
 * 確定フォーマット (spec `admin/data/uploads/supply-48-data.md` §1、51 列):
 * 「取引ID, 日付, コマ1〜48, delete」の横持ち・値 1 系列
 * (kWh 非負整数)。タブ / カンマ両対応 (タブ優先判定)、ヘッダー省略可
 * (先頭セル「取引ID」で判定)、日付は YYYY/M/D または YYYY-MM-DD 受理。
 * build 側の `escapeCsvCell` (RFC 4180 クォート + CWE-1236 のアポストロフィ前置) を
 * そのまま読み戻せるよう、parse 側はクォート解釈とアポストロフィ除去を対で持つ。
 *
 * parse 責務は形式のみ (区切り判定 / ヘッダー行 skip / 全列空値行の読み飛ばし /
 * 列数 51 チェック / 非負整数チェック / delete 列の 0-1 チェック /
 * 日付 2 形式パース / ファイル内重複検出)。業務判定
 * (取引解決 / GC / 差分) は持たない — それは backend (ImportSupply48Data)
 * の責務。
 *
 * `pages/Supply48Data/components/CsvImportModal` (既存の textarea 貼り付け方式) と
 * `pages/Supply48DataCsvUpload` (専用ウィザード画面) の 2 箇所から使う共有純関数のため
 * `src/models/` に配置する (frontend/apps/ebalance/CLAUDE.md 「複数ページ再利用の純関数は
 * src/models/」規約)。
 */

/** parse 結果 1 行。 error があれば送信対象外 (行番号付きで画面表示)。 */
export type ParsedCsvImportRow = {
  rowNumber: number
  publicTradeId: string
  /** YYYY-MM-DD に正規化した受渡日文字列。 */
  deliveryDate: string
  /** 48 要素。 null = 空セル (= 変更しない)。 delete=1 の行では無視される (D12)。 */
  slots: Array<number | null>
  /**
   * true = 51 列目 delete が `1`（対象 (取引ID, 日付) の 48 コマを全て論理削除、
   * AC-SUPPLY-026）。コマ列指定の有無にかかわらず優先する。
   */
  delete: boolean
  error?: string
}

// 取引ID + 日付 + 48 コマ + delete = 51 列。
const EXPECTED_COLUMNS = 2 + KOMA_COUNT + 1

/**
 * エクスポート時に出力するヘッダー行のセル。
 *
 * `readSupply48DataCsv` が受理する spec 確定フォーマット (取引ID, 日付,
 * コマ 1〜48, delete の 51 列、`docs/specs/admin/data/uploads/supply-48-data.md`
 * §1) と同一にすることで「エクスポート → Excel 編集 → CSV アップロード画面」
 * の往復を成立させる (= parse と build を同ファイルに置き、列構成の drift を防ぐ、D10)。
 * delete 列は常に空セルで出力する (エクスポートは削除フラグを持たない)。
 */
export const SUPPLY48_CSV_HEADER_CELLS: readonly string[] = [
  '取引ID',
  '日付',
  ...Array.from({ length: KOMA_COUNT }, (_, i) => `コマ${i + 1}`),
  'delete',
]

/**
 * 英語表示時のヘッダー行。 spec `admin/data/uploads/supply-48-data.md` の
 * 列定義表 (英語ヘッダー名) と、「eCloud 個人設定で表示言語を英語にした場合、
 * CSV ダウンロードファイル名・ヘッダー行は英語表記になる」 に従う。
 * 取込側は日英どちらのヘッダーも受理する (AC-SPS-031、 `readSupply48DataCsv`)。
 */
export const SUPPLY48_CSV_HEADER_CELLS_EN: readonly string[] = [
  'PublicTradeId',
  'DeliveryDate',
  ...Array.from({ length: KOMA_COUNT }, (_, i) => `Slot${i + 1}`),
  'Delete',
]

/**
 * `escapeCsvCell` が数式インジェクション対策 (CWE-1236) で前置したアポストロフィ
 * を取り除く (エクスポート出力の逆変換)。 前置されるのは `' = + - @` で始まる
 * 値のみなので除去も同じ条件に限定する。 エクスポートは `'=X` を `''=X` にする
 * ので、 アポストロフィ自体を含む取引ID も往復で壊れない。 一方で手書き CSV の
 * `'=X` (エクスポート由来でないアポストロフィ) と `=X` のガードは区別できない
 * (= 相対取引契約の新規に登録する取引IDは AC-DUB-064 で先頭が `' = + - @` の値を
 * 登録できないので衝突しない。 規則より前に登録された旧い取引IDは残りうる。
 * 他の値の恒久策は issue #10432)。
 */
function stripFormulaGuard(cell: string): string {
  return /^'\s*['=+\-@]/.test(cell) ? cell.slice(1) : cell
}

/**
 * "YYYY/M/D" または "YYYY-MM-DD" を "YYYY-MM-DD" に正規化する。
 * 2026/02/30 のような暦上実在しない日付も取込前に弾く (issue #4829)。
 */
function parseDeliveryDate(cell: string): string | undefined {
  const m = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/.exec(cell.trim())
  if (m === null) return undefined
  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  if (!isValidCivilDate({ year, month, day })) return undefined
  const mm = String(month).padStart(2, '0')
  const dd = String(day).padStart(2, '0')
  return `${year}-${mm}-${dd}`
}

/**
 * 1 コマのセル値を parse する。 空文字 (trim 後) = null (= 変更しない、
 * D4)。 非負整数以外はエラー (undefined) を返す。
 */
function parseSlotCell(cell: string): { value: number | null } | undefined {
  const trimmed = cell.trim()
  if (trimmed === '') return { value: null }
  if (!/^\d+$/.test(trimmed)) return undefined // 負値・小数・非数値を拒否
  const n = Number(trimmed)
  if (!Number.isSafeInteger(n)) return undefined
  return { value: n }
}

/**
 * delete 列 (51 列目) のセル値を parse する。 空欄 / `0` = false、 `1` = true。
 * それ以外は不正 (undefined、spec QA 観点「delete 列に 0/1 以外はエラー」)。
 */
function parseDeleteCell(cell: string): boolean | undefined {
  const trimmed = cell.trim()
  if (trimmed === '' || trimmed === '0') return false
  if (trimmed === '1') return true
  return undefined
}

/**
 * 全列が空値のレコードかどうか (AC-DUC-036 [P0]: 「当該行を存在しないものとして無視し、
 * 必須項目欠落エラー・差分プレビューのいずれにも計上しない」)。
 *
 * レコード全体を連結して `.trim()` で空判定しない — delete 列 (51 列目) が空セルの行は
 * タブ区切りで末尾がタブ + 空文字列になり、タブは Unicode 空白なので trim が末尾のタブごと
 * 消して「50 列」に化ける。セル単位で空判定することで、末尾の空セルを保ったまま
 * 「実質空行」だけを除外する。
 */
function isAllCellsBlank(cells: string[]): boolean {
  return cells.every((cell) => cell.trim() === '')
}

/** `readSupply48DataCsv` の結果。 */
export type Supply48DataCsvReadResult = {
  rows: ParsedCsvImportRow[]
  /**
   * 囲み (ダブルクォート) が正しくない行の物理行番号 (AC-DUC-045)。入っている時、
   * `rows` は空で、ファイル全体を Step1 で拒否する。
   */
  quoteErrorRow?: number
}

/**
 * CSV テキストを行単位の取込候補に parse し、囲みが正しくないファイルを検出して返す。
 *
 * - 囲み内の改行は 1 レコードとして読む (AC-DUC-043)
 * - 区切り判定: レコードごとに、囲みの外にタブがあればタブ、無ければカンマ
 *   (`supply-48-data.md` の区切り文字)
 * - ヘッダー行 skip: 先頭セルが「取引ID」を含めばヘッダーとみなす
 * - 列数 51 チェック: 一致しない行は行エラー
 * - 非負整数チェック: 数値以外 / 負値 / 小数はセル単位でエラー
 * - delete 列: 空欄 / 0 / 1 を受理、他はエラー
 * - 日付 2 形式パース: YYYY/M/D または YYYY-MM-DD
 * - ファイル内重複検出: 同一 (取引ID, 日付) の行は両方エラー
 */
export function readSupply48DataCsv(
  csvText: string,
): Supply48DataCsvReadResult {
  const read = readCsvRecords(csvText, 'auto')
  if (!read.ok) return { rows: [], quoteErrorRow: read.quoteErrorRow }
  // AC-DUC-036: 全列空値の行 (`,,,,` / タブのみ / 空白のみ) を読み飛ばす。
  const lines = read.records
    .map((r) => r.cells)
    .filter((c) => !isAllCellsBlank(c))
  return { rows: buildRows(lines) }
}

function buildRows(lines: string[][]): ParsedCsvImportRow[] {
  const firstCells = lines[0] ?? []
  // 英語表示でダウンロードすると 1 列目が `PublicTradeId` になる。
  // spec `admin/data/uploads/supply-48-data.md`「アップロード時は日本語ヘッダー・
  // 英語ヘッダーのいずれも受理する (AC-SPS-031)」。 受けないとヘッダー行が
  // データ行として扱われ、 英語表示ユーザーの往復が必ず 1 行エラーになる。
  const firstCell = firstCells[0] ?? ''
  const isHeader =
    firstCell.includes('取引ID') || firstCell.includes('PublicTradeId')
  const dataLines = isHeader ? lines.slice(1) : lines

  const rows: ParsedCsvImportRow[] = dataLines.map((cols, i) => {
    const rowNumber = i + 1
    const fail = (message: string): ParsedCsvImportRow => ({
      rowNumber,
      publicTradeId: stripFormulaGuard(cols[0]?.trim() ?? ''),
      deliveryDate: '',
      slots: [],
      delete: false,
      error: message,
    })

    if (cols.length !== EXPECTED_COLUMNS) {
      // AC-SUPPLY-009 の固定文言 (行番号は表示側が `[N]` で付す)。
      return fail(
        `列数が不正です（${cols.length}列、期待値 ${EXPECTED_COLUMNS}列）`,
      )
    }

    const publicTradeId = stripFormulaGuard(cols[0].trim())
    if (publicTradeId === '') return fail('取引IDが空です')

    const deliveryDate = parseDeliveryDate(cols[1])
    if (deliveryDate === undefined) return fail(`日付不正: ${cols[1]}`)

    // delete 列はコマ列より先に読む — delete=1 の行はコマ列を検証しないため
    // (AC-SUPPLY-026「delete=1 の行のコマ列指定は無視する」/ D12)、
    // 検証の可否が delete フラグに依存する。
    const deleteCell = cols[2 + KOMA_COUNT]
    const deleteFlag = parseDeleteCell(deleteCell)
    if (deleteFlag === undefined) {
      return fail(`delete列の値が不正です: ${deleteCell}`)
    }

    const slotCells = cols.slice(2, 2 + KOMA_COUNT)
    const slots: Array<number | null> = []
    for (let k = 0; k < slotCells.length; k++) {
      const parsed = parseSlotCell(slotCells[k])
      if (parsed === undefined) {
        // delete=1 では不正コマ値を行エラーにしない。backend の delete 分岐は
        // negativeSlotRowError を通さず DELETED を返す (D12) ので、ここで弾くと
        // 「消したいのに消せない行」が生まれ AC-SUPPLY-026 に反する。値は backend が
        // 無視するが proto が 48 要素を要求するため長さは保ち null を詰める。
        if (deleteFlag) {
          slots.push(null)
          continue
        }
        return fail(`コマ${k + 1}の値が不正です: ${slotCells[k]}`)
      }
      slots.push(parsed.value)
    }

    return { rowNumber, publicTradeId, deliveryDate, slots, delete: deleteFlag }
  })

  // ファイル内重複検出 (D5): 同一 (取引ID, 日付) の行は両方エラーにする。
  const seen = new Map<string, number>() // key -> first-seen index
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]
    if (r.error !== undefined) continue
    const key = `${r.publicTradeId}\u0000${r.deliveryDate}`
    const firstIdx = seen.get(key)
    if (firstIdx !== undefined) {
      rows[i] = {
        ...r,
        error: `同一 (取引ID, 日付) の行が ${rows[firstIdx].rowNumber}行目と重複しています`,
      }
      if (rows[firstIdx].error === undefined) {
        rows[firstIdx] = {
          ...rows[firstIdx],
          error: `同一 (取引ID, 日付) の行が ${r.rowNumber}行目と重複しています`,
        }
      }
      continue
    }
    seen.set(key, i)
  }

  return rows
}

/**
 * CSV エクスポート 1 行分のデータ (供給源 × 受渡日)。
 *
 * `ParsedCsvImportRow` の逆向き (= 出力用) の型。行番号・エラーを持たない点と、
 * slots が常に 48 要素である点が異なる (null = 保存値なしの空セル)。
 */
export type Supply48CsvExportRow = {
  /** 取引ID (PublicTradeId)。 */
  publicTradeId: string
  /** "YYYY-MM-DD" の受渡日文字列。 */
  deliveryDate: string
  /** 48 要素。 null = 空セル (取込時は「変更しない」として扱われる)。 */
  slots: Array<number | null>
}

/**
 * エクスポート行から CSV 本文を組み立てる (F1 純関数、AC-S4D-007)。
 *
 * 区切りはカンマ、改行は `\r\n` (RFC 4180)。BOM は付けない
 * (= `~/libs/downloadCsv` が保存時に付与する。二重 BOM を避けるための分担)。
 * rows が空でもヘッダー行のみを返す (= 出力対象 0 件でもテンプレートとして使える)。
 */
export function buildSupply48DataCsv(
  rows: readonly Supply48CsvExportRow[],
  options?: { isEnglish?: boolean },
): string {
  const headerLine = formatSupply48CsvHeader(options?.isEnglish === true)
  // D10: エクスポートは削除フラグを持たないので delete 列は常に空
  const bodyLines = rows.map((row) => formatSupply48CsvRow(row, false))
  return [headerLine, ...bodyLines].join('\r\n')
}

/**
 * 雛形の記入例 1 行分。エクスポート行と違い delete 列を持つ (記入例で削除の
 * 書き方を示すため)。
 */
export type Supply48CsvTemplateRow = {
  publicTradeId: string
  /** CSV にそのまま書く日付文字列 (取込は YYYY/M/D と YYYY-MM-DD を受理)。 */
  deliveryDate: string
  /** 48 要素。 null = 空セル。 */
  slots: Array<number | null>
  delete: boolean
}

/**
 * 雛形 CSV 本文 (ヘッダー + 記入例行、AC-DUC-031)。ヘッダーは表示言語に従う
 * (spec §1「英語ヘッダー対応」)。書き出し規則は `buildSupply48DataCsv` と共有する
 * — 雛形だけ別の規則で書くと、DL した雛形が自画面の取込で弾かれる drift を
 * 作りうるため。
 */
export function buildSupply48DataCsvTemplate(
  rows: readonly Supply48CsvTemplateRow[],
  options?: { isEnglish?: boolean },
): string {
  const headerLine = formatSupply48CsvHeader(options?.isEnglish === true)
  const bodyLines = rows.map((row) => formatSupply48CsvRow(row, row.delete))
  return [headerLine, ...bodyLines].join('\r\n')
}

function formatSupply48CsvHeader(isEnglish: boolean): string {
  const cells = isEnglish
    ? SUPPLY48_CSV_HEADER_CELLS_EN
    : SUPPLY48_CSV_HEADER_CELLS
  return cells.map(escapeCsvCell).join(',')
}

function formatSupply48CsvRow(
  row: Pick<Supply48CsvExportRow, 'publicTradeId' | 'deliveryDate' | 'slots'>,
  deleteFlag: boolean,
): string {
  return [
    escapeCsvCell(row.publicTradeId),
    escapeCsvCell(row.deliveryDate),
    ...row.slots.map((v) => (v === null ? '' : String(v))),
    deleteFlag ? '1' : '',
  ].join(',')
}

/**
 * ParsedCsvImportRow を送信用の ImportSupply48DataRow に変換する
 * (slots は常に 48 要素、D2)。
 *
 * `pages/Supply48Data` (グリッド画面) と `pages/Supply48DataCsvUpload`
 * (専用ウィザード画面) の 2 箇所が ImportSupply48Data 呼び出し前に使う
 * 共有変換関数。
 */
export function toImportRow(row: ParsedCsvImportRow): ImportSupply48DataRow {
  const [year, month, day] = row.deliveryDate.split('-').map(Number)
  return create(ImportSupply48DataRowSchema, {
    rowNumber: row.rowNumber,
    publicTradeId: row.publicTradeId,
    deliveryDate: create(DateSchema, { year, month, day }),
    slots: row.slots.map((v) =>
      create(Supply48SlotValueSchema, { value: v ?? undefined }),
    ),
    delete: row.delete,
  })
}
